/**
 * Avatar Catalog API
 *
 * Endpoints:
 * - User: GET /avatars (list active), GET/PUT /avatar-selection (own selection)
 * - Superadmin: CRUD on /admin/avatars + set-default
 */

import apiClient from './api';
import type { Avatar, AvatarCreateRequest, AvatarUpdateRequest } from '@/types/avatar';

// ============================================================
// User-facing
// ============================================================

export const listAvatars = async (): Promise<Avatar[]> => {
  const response = await apiClient.get<{ avatars: Avatar[] }>('/avatars');
  return response.data.avatars;
};

export const getMyAvatarSelection = async (): Promise<{
  selected_avatar_id: number | null;
  avatar: Avatar | null;
}> => {
  const response = await apiClient.get<{
    selected_avatar_id: number | null;
    avatar: Avatar | null;
  }>('/settings/avatar-selection');
  return response.data;
};

export const setMyAvatarSelection = async (
  avatarId: number | null
): Promise<{ success: boolean; selected_avatar_id: number | null }> => {
  const response = await apiClient.put<{
    success: boolean;
    selected_avatar_id: number | null;
  }>('/settings/avatar-selection', { avatar_id: avatarId });
  return response.data;
};

// ============================================================
// Admin (superadmin) - catalog management
// ============================================================

export const adminListAvatars = async (): Promise<Avatar[]> => {
  const response = await apiClient.get<{ avatars: Avatar[] }>('/admin/avatars');
  return response.data.avatars;
};

export const adminCreateAvatar = async (
  payload: AvatarCreateRequest
): Promise<Avatar> => {
  const response = await apiClient.post<{ success: boolean; avatar: Avatar }>(
    '/admin/avatars',
    payload
  );
  return response.data.avatar;
};

export const adminUpdateAvatar = async (
  avatarId: number,
  payload: AvatarUpdateRequest
): Promise<Avatar> => {
  const response = await apiClient.put<{ success: boolean; avatar: Avatar }>(
    `/admin/avatars/${avatarId}`,
    payload
  );
  return response.data.avatar;
};

export const adminSetDefaultAvatar = async (avatarId: number): Promise<Avatar> => {
  const response = await apiClient.post<{ success: boolean; avatar: Avatar }>(
    `/admin/avatars/${avatarId}/default`
  );
  return response.data.avatar;
};

export const adminDeleteAvatar = async (avatarId: number): Promise<void> => {
  await apiClient.delete(`/admin/avatars/${avatarId}`);
};
